import type {
  DesignLinkImportNotice,
  DesignLinkImportValidationRow,
  DesignLinkSwitchNotice,
  SwitchValidationRow
} from '@/components/building-block-form/utils/switchValidation'
import type { BuildingBlockDto } from '@/stores/buildingBlock'
import type { QuantityBuildingBlock } from '@/stores/type/quantityBuildingBlock.type'
import { ISSUE_SEVERITIES, type IssueSeverity } from '@/constants/issueSeverity'

export type DesignLinkUnitMismatch = {
  quantityId: number
  quantityUnit: string
  buildingBlockId: number
  buildingBlockUnit: string
  key: string
}

export type DesignLinkUnitMismatchByQuantityId = Record<number, DesignLinkUnitMismatch>

export type DesignLinkImportIssue = {
  severity: IssueSeverity
  rowName: string
  unit: string
  title: string
  summary: string
  fieldDiffs: string[]
}

export type DesignLinkImportIssueByQuantityId = Record<number, DesignLinkImportIssue>

export const buildSelectedBlockIdSet = (
  buildingBlocks: BuildingBlockDto[],
  quantityRows: QuantityBuildingBlock[]
) => {
  const selectedIds = new Set<number>()

  for (const buildingBlock of buildingBlocks) {
    if (buildingBlock.selected) {
      selectedIds.add(buildingBlock.id)
    }
  }

  for (const quantity of quantityRows) {
    if (typeof quantity.building_block === 'number') {
      selectedIds.add(quantity.building_block)
    }
  }

  return selectedIds
}

export const resolveBuildingBlockUnit = (
  buildingBlocks: BuildingBlockDto[],
  buildingBlockId: number | null | undefined
): string => {
  const block = buildingBlocks.find((item) => item.id === buildingBlockId)
  return block ? block.unit : ''
}

const normalizeUnit = (unit: string | null | undefined): string => {
  return (unit ?? '').trim().toLowerCase()
}

export const buildDesignLinkUnitMismatchKey = (
  quantityId: number,
  quantityUnit: string,
  buildingBlockId: number,
  buildingBlockUnit: string
): string => {
  return [
    quantityId,
    normalizeUnit(quantityUnit),
    buildingBlockId,
    normalizeUnit(buildingBlockUnit)
  ].join('::')
}

export const resolveDesignLinkUnitMismatch = (
  quantity: QuantityBuildingBlock,
  buildingBlocks: BuildingBlockDto[]
): DesignLinkUnitMismatch | null => {
  if (typeof quantity.building_block !== 'number') {
    return null
  }

  const buildingBlockUnit = resolveBuildingBlockUnit(buildingBlocks, quantity.building_block)
  if (!normalizeUnit(quantity.unit) || !normalizeUnit(buildingBlockUnit)) {
    return null
  }

  if (normalizeUnit(quantity.unit) === normalizeUnit(buildingBlockUnit)) {
    return null
  }

  return {
    quantityId: quantity.id,
    quantityUnit: quantity.unit,
    buildingBlockId: quantity.building_block,
    buildingBlockUnit,
    key: buildDesignLinkUnitMismatchKey(
      quantity.id,
      quantity.unit,
      quantity.building_block,
      buildingBlockUnit
    )
  }
}

const uniqueByMaterialAndBlock = (rows: SwitchValidationRow[]) => {
  const uniqueRows: Record<string, string> = {}
  for (const row of rows) {
    const key = `${row.material_id}-${row.block_id}`
    if (uniqueRows[key] === undefined) {
      uniqueRows[key] = row.product_name
    }
  }
  return Object.values(uniqueRows)
}

export const buildDesignLinkMissingMessages = (
  notice: DesignLinkSwitchNotice | null | undefined
): string[] => {
  const rows = notice?.missingRows ?? []
  return uniqueByMaterialAndBlock(rows).map(
    (productName) => `Affected material ${productName} is missing in the selected database.`
  )
}

export const buildDesignLinkChangedMessages = (
  notice: DesignLinkSwitchNotice | null | undefined
): string[] => {
  const rows = notice?.changedRows ?? []
  return uniqueByMaterialAndBlock(rows).map(
    (productName) => `Affected material ${productName} has updated values in the selected database.`
  )
}

export const resolveDesignLinkNoticeTitle = (
  notice: DesignLinkSwitchNotice | null | undefined
): string => {
  const base = notice?.baseDatabaseName
  const target = notice?.targetDatabaseName
  if (base && target) {
    return `Some design links are affected by switching database from ${base} to ${target}.`
  }
  return 'Some design links are affected by environmental database changes.'
}

const FIELD_LABELS: Record<string, string> = {
  unit: 'Unit',
  code: 'Code',
  attribute: 'Attribute',
  quantity: 'Quantity'
}

const formatFieldValue = (fieldName: string, raw: unknown): string => {
  const rawText = String(raw ?? '-')
  const baseFieldName = fieldName.replace(/_\d+$/, '')
  if (baseFieldName === 'quantity') {
    const numericValue = parseFloat(rawText)
    if (!isNaN(numericValue)) {
      return parseFloat(numericValue.toFixed(2)).toString()
    }
  }
  return rawText
}

const formatImportIssueFieldLabel = (fieldName: string): string => {
  const indexedMatch = fieldName.match(/^(code|attribute|quantity|unit)_(\d+)$/)
  if (indexedMatch) return `${FIELD_LABELS[indexedMatch[1]]} (row ${indexedMatch[2]})`

  if (fieldName in FIELD_LABELS) return FIELD_LABELS[fieldName]

  const removedMatch = fieldName.match(/^row_(\d+)_removed$/)
  if (removedMatch) return `Row ${removedMatch[1]} (removed)`
  const addedMatch = fieldName.match(/^row_(\d+)_added$/)
  if (addedMatch) return `Row ${addedMatch[1]} (added)`

  return `${fieldName.slice(0, 1).toUpperCase()}${fieldName.slice(1)}`
}

const buildImportIssueFieldDiffs = (row: DesignLinkImportValidationRow): string[] => {
  const changedFields = row.changed_fields ?? {}
  return Object.entries(changedFields).map(([fieldName, values]) => {
    const baseValue = formatFieldValue(fieldName, values?.base)
    const targetValue = formatFieldValue(fieldName, values?.target)
    return `${formatImportIssueFieldLabel(fieldName)}: ${baseValue} -> ${targetValue}`
  })
}

export const buildDesignLinkImportMissingIssueMap = (
  notice: DesignLinkImportNotice | null | undefined
): DesignLinkImportIssueByQuantityId => {
  const rows = notice?.missingRows ?? []
  const issues: DesignLinkImportIssueByQuantityId = {}

  for (const row of rows) {
    issues[Number(row.quantity_row_id)] = {
      severity: ISSUE_SEVERITIES.MISSING,
      rowName: row.row_name,
      unit: row.unit,
      title: 'Missing row in uploaded quantity file',
      summary: row.block_name
        ? `Quantity row ${row.row_name} is missing in the uploaded file. Previous building block link: ${row.block_name}.`
        : `Quantity row ${row.row_name} is missing in the uploaded file.`,
      fieldDiffs: []
    }
  }

  return issues
}

export const buildDesignLinkImportChangedIssueMap = (
  notice: DesignLinkImportNotice | null | undefined
): DesignLinkImportIssueByQuantityId => {
  const rows = notice?.changedRows ?? []
  const issues: DesignLinkImportIssueByQuantityId = {}

  for (const row of rows) {
    issues[Number(row.quantity_row_id)] = {
      severity: ISSUE_SEVERITIES.CHANGED,
      rowName: row.row_name,
      unit: String(row.changed_fields?.unit?.target ?? row.unit ?? ''),
      title: 'Changed values from previous quantity import',
      summary: `Affected quantity row ${row.row_name}`,
      fieldDiffs: buildImportIssueFieldDiffs(row)
    }
  }

  return issues
}

export const resolveDesignLinkImportIssueClass = (
  issue: DesignLinkImportIssue | null | undefined
): string => {
  if (!issue) return ''
  return issue.severity === ISSUE_SEVERITIES.MISSING
    ? 'design-row--import-missing'
    : 'design-row--import-changed'
}

export const resolveDesignLinkImportIssueChipColor = (
  issue: DesignLinkImportIssue | null | undefined
): string => {
  if (!issue) return 'default'
  return issue.severity === ISSUE_SEVERITIES.MISSING ? 'error' : 'warning'
}
